/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import { Bell, Megaphone, Wrench, MessageSquare, X } from 'lucide-react';
import { formatDateTimeAR } from '@/utils/dateFormat';
import './NotificationsPanel.css';

export default function NotificationsPanel({
  announcements = [],
  claims = [],
  directThreads = [],
  userId,
  language = 'es',
  items: providedItems,
  onClose,
  onAnnouncementClick,
  onClaimClick,
  onDirectThreadClick,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [onClose]);

  const unreadAnnouncements = announcements.filter(a => !a.read_by?.includes(userId));
  const activeClaims = claims.filter(c => c.status !== 'closed' && Number(c.unread_count || 0) > 0);
  const unreadDirectThreads = directThreads.filter(thread => (thread.unread_guest || 0) > 0);

  const fallbackItems = [
    ...unreadAnnouncements.map(a => ({
      kind: 'announcement',
      id: a.id,
      title: a.title,
      body: a.content,
      at: a.created_at,
    })),
    ...activeClaims.map(c => ({
      kind: 'claim',
      id: c.id,
      status: c.status,
      claim_type: c.claim_type,
      title: c.status === 'resolved'
        ? (language === 'es' ? `Reclamo resuelto: ${c.title}` : `Claim resolved: ${c.title}`)
        : (language === 'es' ? `Reclamo en progreso: ${c.title}` : `Claim in progress: ${c.title}`),
      body: c.last_message_text || c.admin_response || c.description,
      at: c.updated_at || c.created_at,
      unreadCount: Number(c.unread_count || 0),
    })),
    ...unreadDirectThreads.map(thread => ({
      kind: 'direct-message',
      id: thread.id,
      thread,
      title: language === 'es' ? 'Mensaje de Administración' : 'Message from Administration',
      body: thread.last_message_text,
      at: thread.last_message_at,
      unreadCount: thread.unread_guest,
    })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 12);
  const items = providedItems || fallbackItems;

  return (
    <div
      ref={ref}
      className="notifications-panel fixed sm:absolute inset-x-2 sm:top-[calc(100%+4px)] sm:inset-x-auto sm:right-4 sm:left-auto z-50 w-auto sm:w-80 max-w-[calc(100vw-16px)] overflow-hidden"
      style={{ top: 'max(4.5rem, calc(4rem + env(safe-area-inset-top, 0px)))' }}
      data-testid="notifications-panel"
    >
      <div className="notifications-panel__header">
        <h3 className="notifications-panel__title">
          <Bell className="w-4 h-4" />
          {language === 'es' ? 'Notificaciones' : 'Notifications'}
        </h3>
        <button
          onClick={onClose}
          className="notifications-panel__close"
          aria-label="Close"
          data-testid="notifications-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="notifications-panel__scroll max-h-[60vh] overflow-y-auto">
        {items.length === 0 ? (
          <div className="notifications-panel__empty">
            <Bell className="notifications-panel__empty-icon" />
            <p className="notifications-panel__empty-title">
              {language === 'es' ? 'No hay novedades por ahora' : 'No notifications right now'}
            </p>
          </div>
        ) : (
          <ul className="notifications-panel__list">
            {items.map((item) => (
              <li key={`${item.kind}-${item.id}`}>
                <button
                  type="button"
                  onClick={async () => {
                    if (item.kind === 'announcement') {
                      await onAnnouncementClick(item.id);
                    } else if (item.kind === 'claim') {
                      onClaimClick(item.id, item.status);
                    } else {
                      onClose();
                      onDirectThreadClick(item.thread);
                      return;
                    }
                    onClose();
                  }}
                  className="notifications-panel__item w-full flex items-start gap-3 text-left transition-colors"
                  data-testid={`notif-${item.kind}-${item.id}`}
                >
                  <span className={`notifications-panel__icon w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    item.kind === 'announcement'
                      ? 'icon-pill-blue'
                      : item.kind === 'direct-message'
                        ? 'icon-pill-violet notifications-panel__icon--direct-message'
                        : `icon-pill-violet notifications-panel__icon--claim-${item.claim_type || 'other'}`
                  }`}>
                    {item.kind === 'announcement'
                      ? <Megaphone className="w-4 h-4" />
                      : item.kind === 'direct-message'
                        ? <MessageSquare className="w-4 h-4" />
                        : <Wrench className="w-4 h-4" />}
                  </span>
                  <div className="notifications-panel__copy flex-1 min-w-0">
                    <p className="notifications-panel__item-title line-clamp-1">{item.title}</p>
                    {item.body && <p className="notifications-panel__item-body line-clamp-2">{item.body}</p>}
                    <p className="notifications-panel__item-date">
                      {formatDateTimeAR(item.at, language === 'es' ? 'es-AR' : 'en-US')}
                    </p>
                  </div>
                  {item.kind === 'announcement' && <span className="notifications-panel__unread-dot" aria-label={language === 'es' ? 'No leído' : 'Unread'} />}
                  {(item.kind === 'direct-message' || item.kind === 'claim') && item.unreadCount > 0 && (
                    <span className="notifications-panel__unread-count" aria-label={language === 'es' ? 'Mensajes no leídos' : 'Unread messages'} data-testid={`notif-unread-${item.kind}-${item.id}`}>
                      {item.unreadCount}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
